import { ConfigProvider, Flex, Input, message, Spin } from 'antd'
import locale from 'antd/es/date-picker/locale/ko_KR'
import 'dayjs/locale/ko'
import React, { useEffect, useState } from 'react'
import { useRecoilState } from 'recoil'
import Swal from 'sweetalert2'
import {
  completeStandaloneRoutine,
  deleteStandaloneRoutine,
  getStandaloneRoutineItem,
  incompleteStandaloneRoutine,
  toggleRoutineItemTodoItem,
  updateStandaloneRoutine,
} from '../../../api/standalone.api'
import ColorDropDown from '../../../components/apos/ColorDropDown'
import AopsCommonModalLayout from '../../../components/apos/modal/AopsCommonModalLayout'
import ModalStatusButtonGroup from '../../../components/apos/modal/ModalStatusButtonGroup'
import { userState } from '../../../store/user'
import '../StandaloneSchedulePage.scss'
import FormSection from '../ui/FormSection'
import RoutineSelector from '../ui/RoutineSelector'
import SubTaskManager from '../ui/SubTaskManager'

// 상세 팝업 (create 용도 X )
const StandaloneRoutineItemPopup = ({ id, open, onClose, onSuccess }) => {
  const [user] = useRecoilState(userState)
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    routineFrequency: null, // 'DAILY', 'WEEKLY', 'MONTHLY'
    routineType: null, // 'DAY_OF_WEEK' or 'DAY_OF_MONTH'
    selectedDaysOfWeek: [], // DayOfWeek 이넘 배열 ['MONDAY', 'TUESDAY', ...]
    dayOfMonth: null, // null or 1-31 숫자
    todoItems: [],
    completed: false,
    colorCode: '#FFC700',
  })

  const fetchRoutineItemDetail = async () => {
    if (!id || !user?.id) return

    setIsLoading(true)
    try {
      const res = await getStandaloneRoutineItem(id, user.id)
      console.log('getStandaloneRoutineItem res::', res)
      if (res.status === 200) {
        const data = res.data
        const info = data.routine
        const routineItem = data.routineItem

        // routineType 결정 로직
        let routineType = null
        if (info.frequency === 'WEEKLY') {
          routineType = 'DAY_OF_WEEK'
        } else if (info.frequency === 'MONTHLY') {
          routineType = 'DAY_OF_MONTH'
        } else if (info.frequency === 'DAILY') {
          routineType = null
        }

        // todoItems를 subTasks 형태로 변환 (checked -> isDone)
        const todoItems =
          data.todoItems?.map((item) => ({
            id: item.id || `existing-${Date.now()}-${Math.random()}`,
            title: item.title || '',
            isDone: item.checked || item.isChecked || false,
          })) || []

        setFormData({
          name: info.title || '',
          routineFrequency: info.frequency || null,
          routineType: routineType,
          selectedDaysOfWeek: Array.isArray(info.daysOfWeek)
            ? info.daysOfWeek
            : [],
          dayOfMonth:
            info.daysOfMonth && info.daysOfMonth.length > 0
              ? info.daysOfMonth[0]
              : null,
          todoItems: todoItems,
          completed: routineItem?.completed || false,
          createdByName: info?.createdByName || '',
          routineId: info?.id,
          colorCode: info.colorCode || '#FFC700',
        })
      }
    } catch (error) {
      console.error('fetchRoutineItemDetail error::', error)
      message.error('업무 상세 조회에 실패했습니다.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (open && id) {
      fetchRoutineItemDetail()
    }
  }, [open, id])

  const getDayOfMonthError = () => {
    if (
      formData.routineFrequency === 'MONTHLY' &&
      formData.dayOfMonth !== null &&
      formData.dayOfMonth !== ''
    ) {
      const dayNum = parseInt(formData.dayOfMonth, 10)
      if (isNaN(dayNum) || dayNum < 0 || dayNum > 31) {
        return '0-31 사이의 숫자를 입력해주세요'
      }
    }
    return null
  }

  const isFormValid = () => {
    // 업무명 필수
    if (!formData.name || formData.name.trim() === '') {
      return false
    }

    // 루틴 주기 필수
    if (!formData.routineFrequency) {
      return false
    }

    // 주기별 필수 조건 확인
    if (formData.routineFrequency === 'WEEKLY') {
      // 매주인 경우 요일 선택 필수
      if (
        !formData.selectedDaysOfWeek ||
        formData.selectedDaysOfWeek.length === 0
      ) {
        return false
      }
    } else if (formData.routineFrequency === 'MONTHLY') {
      // 매월인 경우 일자 필수 (0-31 범위)
      if (formData.dayOfMonth === null || formData.dayOfMonth === '') {
        return false
      }
      const dayNum = parseInt(formData.dayOfMonth, 10)
      if (isNaN(dayNum) || dayNum < 0 || dayNum > 31) {
        return false
      }
    }
    // DAILY인 경우 추가 조건 없음

    return true
  }

  // 수정 저장
  const handleSaveEdit = async () => {
    // 수정 전 확인 모달
    const result = await Swal.fire({
      title: 'Routine 업무 수정',
      html: `
        <div style="text-align: center; padding: 10px 0;">
          <p style="margin-bottom: 10px; text-align: center;">
            루틴 업무를 수정하시겠습니까?
          </p>
          <p style="color: #d33; font-weight: bold; text-align: center;">
            ⚠️ 주의: 완료된 업무까지 포함하여 모든 루틴 업무가 수정됩니다!
          </p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: '수정하기',
      cancelButtonText: '취소',
    })

    if (!result.isConfirmed) {
      return
    }

    if (!isFormValid()) {
      message.error('필수 항목을 모두 입력해주세요.')
      return
    }

    if (!formData.routineId || !user?.id) return

    setIsLoading(true)
    try {
      const param = {
        salusUserId: user.id,
        title: formData.name,
        frequency: formData.routineFrequency, // 'DAILY', 'WEEKLY', 'MONTHLY'
        daysOfWeek:
          formData.routineFrequency === 'WEEKLY'
            ? formData.selectedDaysOfWeek || []
            : [],
        daysOfMonth:
          formData.routineFrequency === 'MONTHLY' &&
          formData.dayOfMonth !== null &&
          formData.dayOfMonth !== ''
            ? [parseInt(formData.dayOfMonth, 10)]
            : [],
        todoItems: formData.todoItems.map((subTask) => ({
          title: subTask.title,
          isChecked: subTask.isDone ?? false,
        })),
        colorCode: formData.colorCode,
      }
      console.log('updateStandaloneRoutine param::', param)
      const res = await updateStandaloneRoutine(formData.routineId, param)
      console.log('updateStandaloneRoutine res::', res)
      if (res.status === 200) {
        message.success('Routine 업무가 수정되었습니다.')
        onSuccess?.() // refetch tracker board
        onClose() // 팝업 닫기
      } else {
        message.error('Routine 업무 수정에 실패했습니다.')
      }
    } catch (error) {
      console.error('handleSaveEdit error::', error)
      message.error('Routine 업무 수정에 실패했습니다.')
    } finally {
      setIsLoading(false)
    }
  }

  // 하위업무 체크 토글
  const handleToggleTodoItem = async (todoItemId) => {
    if (!user?.id) return
    if (formData.completed) {
      message.warning('완료된 업무의 하위업무 상태는 변경할 수 없습니다.')
      return
    }

    try {
      const res = await toggleRoutineItemTodoItem(todoItemId, user.id)
      if (res.status === 200) {
        // 로컬 상태 업데이트
        setFormData((prev) => ({
          ...prev,
          todoItems: prev.todoItems.map((item) =>
            item.id === todoItemId
              ? { ...item, isDone: !(item.isDone ?? false) }
              : item,
          ),
        }))
        onSuccess?.() // refetch tracker board
      }
    } catch (error) {
      console.log('handleToggleTodoItem error::', error)
      message.error('하위업무 상태 변경에 실패했습니다.')
    }
  }

  // 하위업무가 모두 완료되었는지 확인
  const isAllTodoCompleted = () => {
    if (!formData.todoItems || formData.todoItems.length === 0) {
      return true // 하위업무가 없으면 완료로 간주
    }
    return formData.todoItems.every((item) => item.isDone ?? false)
  }

  // 루틴 완료/미완료 토글
  const handleCompleteRoutine = async () => {
    if (!id || !user?.id) return

    try {
      if (formData.completed) {
        // 이미 완료된 상태 -> 미완료로 변경
        const res = await incompleteStandaloneRoutine(id, user.id)
        if (res.status === 200) {
          onClose()
          message.success('Routine 업무가 미완료로 변경되었습니다.')
        }
      } else {
        // 미완료 상태 -> 완료로 변경
        const res = await completeStandaloneRoutine(id, user.id)
        console.log('completeStandaloneRoutine res::', res)
        if (res.status === 200) {
          onClose()
          message.success('Routine 업무가 완료되었습니다.')
        }
      }
      // 로컬 상태 업데이트
      setFormData((prev) => ({
        ...prev,
        completed: !prev.completed,
      }))

      onSuccess?.() // refetch tracker board
    } catch (error) {
      console.log('handleCompleteRoutine error::', error)
      message.error('업무 상태 변경에 실패했습니다.')
    }
  }

  // routineItem이 아니라 routineId(기준정보)를 삭제 -> 모든 routineItem도 삭제됨
  const handleDelete = async () => {
    const routineId = formData.routineId

    const result = await Swal.fire({
      title: 'Routine 업무 삭제',
      text: '모든 루틴 업무가 삭제됩니다. 정말 삭제하시겠습니까?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: '삭제',
      cancelButtonText: '취소',
    })

    if (result.isConfirmed) {
      try {
        const res = await deleteStandaloneRoutine(routineId, user?.id)
        console.log('deleteStandaloneRoutine res::', res)
        if (res.status === 200) {
          message.success('Routine 업무가 삭제되었습니다.')
          onSuccess?.()
          onClose()
        } else {
          message.error('Routine 업무 삭제에 실패했습니다.')
        }
      } catch (error) {
        console.error('handleDelete error::', error)
        message.error('Routine 업무 삭제에 실패했습니다.')
      }
    }
  }

  return (
    <ConfigProvider
      locale={{
        ...locale,
        DatePicker: locale,
      }}
    >
      <AopsCommonModalLayout
        title={'Routine 업무 상세'}
        onClose={onClose}
        open={open}
      >
        <Spin spinning={isLoading}>
          <div className="form-container standalone-task-popup">
            <div className="form-wrapper">
              {/* 명칭 입력 */}
              <FormSection
                label="업무 명"
                required
                helperTitle="Routine 업무란?"
                helperDesc="매일·매주·매월 반복되는 놓치면 안 되는 업무 루틴"
              >
                <Flex flexDirection="row" gap="8px">
                  <ColorDropDown
                    value={formData.colorCode}
                    defaultValue={formData.colorCode}
                    onChange={(value) =>
                      setFormData((prev) => ({ ...prev, colorCode: value }))
                    }
                  />
                  <Input
                    placeholder="업무명을 입력해주세요"
                    className="form-input"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                  />
                </Flex>
              </FormSection>

              {/* 일정 */}
              <FormSection label="일정" required>
                <RoutineSelector
                  routineFrequency={formData.routineFrequency}
                  routineType={formData.routineType}
                  selectedDaysOfWeek={formData.selectedDaysOfWeek}
                  dayOfMonth={formData.dayOfMonth}
                  onFrequencyChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      routineFrequency: value,
                    }))
                  }
                  onTypeChange={(value) =>
                    setFormData((prev) => ({ ...prev, routineType: value }))
                  }
                  onDaysOfWeekChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      selectedDaysOfWeek: value,
                    }))
                  }
                  onDayOfMonthChange={(value) =>
                    setFormData((prev) => ({ ...prev, dayOfMonth: value }))
                  }
                  dayOfMonthError={getDayOfMonthError()}
                  disabled={false}
                />
              </FormSection>

              {/* 담당자 */}
              {formData.createdByName && (
                <FormSection label="담당자" required>
                  <ModalStatusButtonGroup
                    options={[formData.createdByName]}
                    value={formData.createdByName}
                    onChange={() => {}}
                    isRound={true}
                    disabled={true}
                  />
                </FormSection>
              )}

              {/* 하위업무 */}
              <FormSection label="하위업무">
                <SubTaskManager
                  subTitle="하위업무를 추가하고 관리하세요"
                  subTasks={formData.todoItems}
                  onSubTasksChange={(newSubTasks) =>
                    setFormData((prev) => ({ ...prev, todoItems: newSubTasks }))
                  }
                  onToggleCheck={handleToggleTodoItem}
                  disabled={formData.completed}
                />
              </FormSection>
            </div>
          </div>
        </Spin>

        <div className="popup-footer routine-item-popup-footer">
          <div className="routine-item-popup-footer-center">
            <button
              className="btn-submit"
              onClick={handleCompleteRoutine}
              disabled={!formData.completed && !isAllTodoCompleted()}
            >
              {formData.completed ? '완료됨' : '업무 완료'}
            </button>
          </div>
          <div className="routine-item-popup-footer-right">
            <button onClick={handleSaveEdit} className="btn-delete-routine">
              수정
            </button>
            <button onClick={handleDelete} className="btn-delete-routine">
              삭제
            </button>
          </div>
        </div>
      </AopsCommonModalLayout>
    </ConfigProvider>
  )
}

export default StandaloneRoutineItemPopup
